/**
 * Utilities for МОЗ submission form
 * Contains helper functions, type definitions, and business logic
 */

export interface SpecialtyPath {
  path: string[]; // Full hierarchical path: ["Лікарські спеціальності", "Лікарські спеціальності загального профілю", "Педіатрія"]
  name: string; // Leaf specialty name: "Педіатрія"
}

export type EventType =
  | 'Симуляційний тренінг'
  | 'Тренінг з оволодіння практичними навичками'
  | 'Майстер-клас'
  | 'Тренінг'
  | 'Семінар'
  | 'Наукова конференція'
  | 'Науково-практична конференція'
  | 'Конгрес'
  | 'Симпозіум'
  | "З'їзд"
  | 'Електронний навчальний курс'
  | 'Фахова (тематична) школа';

export type EventDirection =
  | 'етичні питання'
  | 'консультування та професійні комунікаційні навички'
  | 'порядок проведення та документування результатів медичного обстеження'
  | 'цифрова компетентність'
  | 'невідкладна (екстрена) медична допомога'
  | 'інфекційний медчиний контроль';

export type EventFormat = 'online' | 'in_person' | 'hybrid' | '';

type EventCategory = 'conference' | 'training' | 'hands-on' | 'online-only';

export const EVENT_TYPES: EventType[] = [
  'Симуляційний тренінг',
  'Тренінг з оволодіння практичними навичками',
  'Майстер-клас',
  'Тренінг',
  'Семінар',
  'Наукова конференція',
  'Науково-практична конференція',
  'Конгрес',
  'Симпозіум',
  "З'їзд",
  'Електронний навчальний курс',
  'Фахова (тематична) школа'
];

export const EVENT_DIRECTIONS: EventDirection[] = [
  'етичні питання',
  'консультування та професійні комунікаційні навички',
  'порядок проведення та документування результатів медичного обстеження',
  'цифрова компетентність',
  'невідкладна (екстрена) медична допомога',
  'інфекційний медчиний контроль'
];

export const LANGUAGES = ['Англійська', 'Українська', 'Інша'];

/**
 * Determines the event category based on event type
 */
export function getEventCategory(eventType: EventType | ''): EventCategory | null {
  if (!eventType) return null;

  const conferenceTypes: EventType[] = [
    'Наукова конференція',
    'Науково-практична конференція',
    'Конгрес',
    'Симпозіум',
    "З'їзд"
  ];

  const trainingTypes: EventType[] = [
    'Семінар',
    'Тренінг',
    'Майстер-клас',
    'Фахова (тематична) школа'
  ];

  const handsOnTypes: EventType[] = [
    'Симуляційний тренінг',
    'Тренінг з оволодіння практичними навичками'
  ];

  const onlineOnlyTypes: EventType[] = ['Електронний навчальний курс'];

  if (conferenceTypes.includes(eventType)) return 'conference';
  if (trainingTypes.includes(eventType)) return 'training';
  if (handsOnTypes.includes(eventType)) return 'hands-on';
  if (onlineOnlyTypes.includes(eventType)) return 'online-only';

  return null;
}

/**
 * Checks if event type requires technical instruments field
 */
export function requiresTechnicalInstruments(eventType: EventType | ''): boolean {
  const category = getEventCategory(eventType);
  return category === 'training' || category === 'hands-on';
}

/**
 * Checks if event type requires educational program file
 */
export function requiresEducationalProgram(eventType: EventType | ''): boolean {
  const category = getEventCategory(eventType);
  return category === 'training' || category === 'hands-on' || category === 'online-only';
}

/**
 * Checks if event type requires organizational committee field
 */
export function requiresOrganizationalCommittee(eventType: EventType | ''): boolean {
  const category = getEventCategory(eventType);
  return category === 'conference';
}

/**
 * Checks if event type supports event format selection
 */
export function supportsEventFormat(eventType: EventType | ''): boolean {
  const category = getEventCategory(eventType);
  return category === 'conference' || category === 'training';
}

/**
 * Gets allowed event formats for event type
 */
export function getAllowedEventFormats(eventType: EventType | ''): EventFormat[] {
  const category = getEventCategory(eventType);

  // Conference and training events support all 3 formats
  if (category === 'conference' || category === 'training') {
    return ['online', 'in_person', 'hybrid'];
  }

  // Hands-on events require physical presence (in-person only)
  if (category === 'hands-on') {
    return ['in_person'];
  }

  // Online-only courses
  if (category === 'online-only') {
    return ['online'];
  }

  return [];
}

/**
 * Gets automatic event format for event types that don't allow selection
 */
export function getAutoEventFormat(eventType: EventType | ''): EventFormat {
  const category = getEventCategory(eventType);

  // Hands-on events require physical presence (in-person only)
  if (category === 'hands-on') {
    return 'in_person';
  }

  // Online-only courses are strictly online
  if (category === 'online-only') {
    return 'online';
  }

  return '';
}

/**
 * Checks if OECD countries field is supported
 */
export function supportsOECDCountries(eventType: EventType | ''): boolean {
  const category = getEventCategory(eventType);
  return category === 'conference';
}

/**
 * Helper function to convert ISO date (yyyy-mm-dd) to European format (dd.mm.yyyy)
 */
export function isoToEuropean(isoDate: string): string {
  if (!isoDate) return '';
  const [year, month, day] = isoDate.split('T')[0].split('-');
  return `${day}.${month}.${year}`;
}

/**
 * Helper function to convert European date (dd.mm.yyyy) to date object for BPR API
 */
export function europeanToDateObject(europeanDate: string): { year: number; month: number; day: number } | null {
  if (!europeanDate) return null;
  const parts = europeanDate.split('.');
  if (parts.length !== 3) return null;

  const [day, month, year] = parts;
  return {
    year: parseInt(year),
    month: parseInt(month),
    day: parseInt(day)
  };
}

/**
 * Validates European date format
 */
export function isValidEuropeanDate(date: string): boolean {
  if (!date) return false;
  const parts = date.split('.');
  if (parts.length !== 3) return false;

  const [day, month, year] = parts.map(p => parseInt(p));

  if (isNaN(day) || isNaN(month) || isNaN(year)) return false;
  if (year < 2000 || year > 2100) return false;
  if (month < 1 || month > 12) return false;
  if (day < 1 || day > 31) return false;

  return true;
}

/**
 * Validates URL format and ensures HTTPS
 */
export function isValidHttpsUrl(url: string): boolean {
  if (!url) return false;
  try {
    const urlObj = new URL(url);
    return urlObj.protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * Generates user-friendly error messages
 */
export function getValidationErrorMessage(field: string, eventType: EventType | ''): string {
  const category = getEventCategory(eventType);

  const messages: Record<string, string> = {
    mozLogin: 'Введіть логін від системи МОЗ (BPR)',
    mozPassword: 'Введіть пароль від системи МОЗ (BPR)',
    eventType: 'Оберіть вид заходу зі списку',
    eventTheme: 'Введіть назву/тему заходу',
    eventGoal: 'Опишіть мету заходу БПР',
    targetAudience: 'Заповніть "Цільові спеціальності" у формі заходу (секція "Основна інформація")',
    academicHours: 'Введіть кількість навчальних годин (може бути дробовим числом)',
    providerLink: 'Введіть HTTPS посилання на інформацію про захід на вашому сайті',
    registrationLink: 'Введіть HTTPS посилання для реєстрації учасників',
    eventLanguages: 'Оберіть хоча б одну мову проведення заходу',
    startDate: 'Введіть дату початку у форматі дд.мм.рррр',
    endDate: 'Введіть дату закінчення у форматі дд.мм.рррр',
    eventFormat: 'Оберіть формат заходу (очна/дистанційна/змішана)',
    eventProgramFile: 'Завантажте програму заходу (PDF)',
    educationalProgramFile: category === 'conference' ? 'Навчальна програма не потрібна для конференцій' : 'Завантажте навчальну програму (PDF)',
    speakers: 'Додайте хоча б одного спікера/доповідача',
    participantCount: 'Введіть заплановану кількість учасників',
    onlineUrl: 'Введіть HTTPS посилання на онлайн-захід',
    country: 'Оберіть країну проведення заходу (секція "Основна інформація")',
    region: 'Оберіть область проведення заходу (секція "Основна інформація")',
    city: 'Введіть місто проведення заходу',
    street: 'Введіть вулицю',
    building: 'Введіть номер будинку'
  };

  return messages[field] || `Заповніть поле: ${field}`;
}

/**
 * Converts flat specialty paths into hierarchical BPR API format
 *
 * Example input:
 * [
 *   { path: ["Лікарські спеціальності", "Лікарські спеціальності загального профілю", "Педіатрія"], name: "Педіатрія" },
 *   { path: ["Лікарські спеціальності", "Лікарські спеціальності спеціалізованого профілю", "Лікарські спеціальності нехірургічного профілю", "Кардіологія"], name: "Кардіологія" }
 * ]
 *
 * Example output:
 * {
 *   "Лікарські спеціальності": {
 *     "Лікарські спеціальності загального профілю": {
 *       "Педіатрія": true
 *     },
 *     "Лікарські спеціальності спеціалізованого профілю": {
 *       "Лікарські спеціальності нехірургічного профілю": {
 *         "Кардіологія": true
 *       }
 *     }
 *   }
 * }
 */
export function convertPathsToHierarchy(paths: SpecialtyPath[]): Record<string, unknown> {
  const result: Record<string, unknown> = {};

  paths.forEach(({ path }) => {
    let current = result;

    path.forEach((segment, index) => {
      const isLeaf = index === path.length - 1;

      if (isLeaf) {
        current[segment] = true;
      } else {
        if (!current[segment] || typeof current[segment] !== 'object') {
          current[segment] = {};
        }
        current = current[segment] as Record<string, unknown>;
      }
    });
  });

  return result;
}

export interface BprCredits {
  bpr_credits_in_person: number | null;
  bpr_credits_online: number | null;
  bpr_credits_speaker: number | null;
  bpr_credits_poster_presentation: number | null;
  bpr_credits_oral_presentation: number | null;
  bpr_credits_course_ua: number | null;
  bpr_credits_course_en: number | null;
}

/**
 * Calculates all 7 BPR credit values based on event type, academic hours, format, and languages.
 */
export function calculateBprCredits(
  eventType: EventType | '',
  academicHours: number | null,
  eventFormat: string,
  eventLanguages: string[]
): BprCredits {
  const h = academicHours;
  const category = getEventCategory(eventType);
  const isInPerson = eventFormat === 'in_person' || eventFormat === 'hybrid';
  const isOnline = eventFormat === 'online' || eventFormat === 'hybrid';

  const round2 = (n: number) => Math.round(n * 100) / 100;

  let bpr_credits_in_person: number | null = null;
  let bpr_credits_online: number | null = null;
  let bpr_credits_speaker: number | null = null;
  let bpr_credits_poster_presentation: number | null = null;
  let bpr_credits_oral_presentation: number | null = null;
  let bpr_credits_course_ua: number | null = null;
  let bpr_credits_course_en: number | null = null;

  if (h != null && category) {
    if (isInPerson) {
      if (category === 'training') bpr_credits_in_person = round2(1.5 * h);
      else if (category === 'hands-on') bpr_credits_in_person = round2(2.0 * h);
      else if (category === 'conference') bpr_credits_in_person = round2(0.5 * h);
    }

    if (isOnline) {
      if (category === 'training') bpr_credits_online = round2(1.0 * h);
      else if (category === 'conference') bpr_credits_online = round2(0.5 * h);
    }

    if (category === 'training') bpr_credits_speaker = round2(2.0 * h);
    else if (category === 'hands-on') bpr_credits_speaker = round2(2.5 * h);

    if (category === 'conference') {
      bpr_credits_poster_presentation = 10;
      bpr_credits_oral_presentation = 15;
    }

    if (category === 'online-only') {
      if (eventLanguages.includes('Українська')) bpr_credits_course_ua = round2(1.0 * h);
      if (eventLanguages.includes('Англійська')) bpr_credits_course_en = round2(1.5 * h);
    }
  }

  return {
    bpr_credits_in_person,
    bpr_credits_online,
    bpr_credits_speaker,
    bpr_credits_poster_presentation,
    bpr_credits_oral_presentation,
    bpr_credits_course_ua,
    bpr_credits_course_en,
  };
}

export interface ParticipantLimit {
  limit: number;
  perTrainer: boolean; // true → limit × number of teachers/trainers (= listed speakers)
}

/**
 * Participant-count limits per event type (МОЗ наказ №650 від 16.04.2025).
 * Types absent from this map have no limit.
 */
export const PARTICIPANT_LIMITS: Partial<Record<EventType, ParticipantLimit>> = {
  'Фахова (тематична) школа': { limit: 12, perTrainer: true },
  'Тренінг': { limit: 20, perTrainer: false },
  'Семінар': { limit: 20, perTrainer: false },
  'Симуляційний тренінг': { limit: 6, perTrainer: true },
  'Тренінг з оволодіння практичними навичками': { limit: 6, perTrainer: true },
};

/**
 * Maximum allowed participants for the type, or null if unlimited.
 * Per-trainer limits multiply by speakerCount (every listed speaker counts
 * as a teacher/trainer).
 */
export function getMaxParticipants(eventType: EventType | '', speakerCount: number): number | null {
  if (!eventType) return null;
  const rule = PARTICIPANT_LIMITS[eventType];
  if (!rule) return null;
  return rule.perTrainer ? rule.limit * Math.max(0, speakerCount) : rule.limit;
}

/**
 * Converts hierarchical BPR API format into flat specialty paths
 *
 * This is the inverse of convertPathsToHierarchy()
 */
export function convertHierarchyToPaths(hierarchy: Record<string, unknown>, parentPath: string[] = []): SpecialtyPath[] {
  const paths: SpecialtyPath[] = [];

  Object.entries(hierarchy).forEach(([key, value]) => {
    const currentPath = [...parentPath, key];

    if (value === true || value === null) {
      // Leaf node
      paths.push({
        path: currentPath,
        name: key
      });
    } else if (typeof value === 'object' && value !== null) {
      // Branch node - recurse
      paths.push(...convertHierarchyToPaths(value as Record<string, unknown>, currentPath));
    }
  });

  return paths;
}
